import { useState, useRef, useEffect } from 'react';
import useStore from '../store/useStore';
import './LooperVisualization.css';

import { getMIDICommand } from '../constants/functions';
import { FaPlay, FaStop, FaMicrophoneSlash, FaRotateLeft, FaTrash, FaBackward, FaForward, FaCircle, FaHandPointer } from "react-icons/fa6";
import { useToast } from './Toast';

// Helper component for quick action buttons
function QuickActionButton({ note, label, desc, sendNoteOn, sendNoteOff, channel, className = '', isActive = false, onToggle = null, icon = null }) {
    const handleClick = () => {
        const ch = channel || 1;

        if (note === 'stop_all') {
            // Combo action resolved from the single source of truth in
            // ../constants/functions.js (stop_all = Stop A + Stop B).
            const cmd = getMIDICommand('stop_all');
            const values = cmd && cmd.values ? cmd.values : [7, 8];

            values.forEach(stopNote => sendNoteOn(stopNote, 127, ch));
            setTimeout(() => {
                values.forEach(stopNote => sendNoteOff(stopNote, ch));
            }, 100);
        } else {
            sendNoteOn(note, 127, ch);
            setTimeout(() => sendNoteOff(note, ch), 100);
        }

        if (onToggle) {
            onToggle();
        }
    };

    return (
        <button
            className={`quick-action-btn ${className} ${isActive ? 'active-glow' : ''}`}
            title={desc || label}
            onClick={handleClick}
        >
            {icon ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', pointerEvents: 'none' }}>
                    <span className="btn-icon">{icon}</span>
                    <span className="btn-label-small" style={{ fontSize: '0.6em', opacity: 0.8, textTransform: 'uppercase' }}>{label}</span>
                </div>
            ) : (
                label
            )}
        </button>
    );
}

function LooperVisualization() {
    // Fine-grained selectors: subscribing to the whole store would re-render
    // this component on every MIDI monitor message (re-render storm under
    // CC/clock streams).
    const deviceState = useStore(state => state.deviceState);
    const deviceType = useStore(state => state.deviceType);
    const isConnected = useStore(state => state.isConnected);
    const currentProfile = useStore(state => state.currentProfile);
    const sendNoteOn = useStore(state => state.sendNoteOn);
    const sendNoteOff = useStore(state => state.sendNoteOff);
    const sendControlChange = useStore(state => state.sendControlChange);
    const autoConnect = useStore(state => state.autoConnect);
    const notify = useToast();
    const [hoveredControl, setHoveredControl] = useState(null);
    // State for toggles
    const [reverseActive, setReverseActive] = useState(false);
    const [speedActive, setSpeedActive] = useState(false);

    // Mode Selection State — notes resolved from the single MIDI map
    // (mode_single 73, mode_song 76, mode_sync 75, mode_multi 74).
    const MODES = [
        { name: 'Single', note: getMIDICommand('mode_single').value },
        { name: 'Song', note: getMIDICommand('mode_song').value },
        { name: 'Sync', note: getMIDICommand('mode_sync').value },
        { name: 'Multi', note: getMIDICommand('mode_multi').value }
    ];
    const [currentModeName, setCurrentModeName] = useState('Single');
    const currentMode = MODES.find(m => m.name === currentModeName);
    const nextMode = MODES[(MODES.findIndex(m => m.name === currentModeName) + 1) % MODES.length];

    // Long-press support for pedals (G10).
    // Timers per pedal + a flag so a completed long-press suppresses the short tap.
    const pedalHoldTimers = useRef({});
    const pedalHoldFired = useRef({});

    // Send the MIDI command produced by getMIDICommand() (note tap, or both notes for a combo).
    const sendPedalCommand = (cmd) => {
        if (!cmd) return;
        const channel = currentProfile?.midiSettings?.channel || 1;
        const tap = (note) => {
            sendNoteOn(note, 127, channel);
            // Send Note Off quickly to mimic a tap
            setTimeout(() => sendNoteOff(note, channel), 100);
        };

        if (cmd.type === 'note') {
            tap(cmd.value);
        } else if (cmd.type === 'combo') {
            cmd.values.forEach(tap);
        }
    };

    // Short tap: fire config.function (existing behaviour).
    const onPedalClick = (pedalKey) => {
        if (!isConnected) return;

        const config = currentProfile.footswitches[pedalKey];
        if (!config) return;

        // Single source of truth for note/CC numbers: ../constants/functions.js
        sendPedalCommand(getMIDICommand(config.function));
    };

    // Begin a press: start the long-press timer if this pedal has a holdFunction.
    const onPedalDown = (pedalKey) => {
        if (!isConnected) return;
        pedalHoldFired.current[pedalKey] = false;

        const config = currentProfile?.footswitches?.[pedalKey];
        if (!config || !config.holdFunction) return; // no hold action -> tap only

        // Clear any stale timer for this pedal first
        if (pedalHoldTimers.current[pedalKey]) {
            clearTimeout(pedalHoldTimers.current[pedalKey]);
        }
        pedalHoldTimers.current[pedalKey] = setTimeout(() => {
            pedalHoldFired.current[pedalKey] = true;
            pedalHoldTimers.current[pedalKey] = null;
            sendPedalCommand(getMIDICommand(config.holdFunction));
        }, 500);
    };

    // End a press: cancel the pending timer. If no long-press fired, perform the short tap.
    const onPedalUp = (pedalKey) => {
        const fired = pedalHoldFired.current[pedalKey];
        if (pedalHoldTimers.current[pedalKey]) {
            clearTimeout(pedalHoldTimers.current[pedalKey]);
            pedalHoldTimers.current[pedalKey] = null;
        }
        if (!fired) {
            onPedalClick(pedalKey);
        }
        pedalHoldFired.current[pedalKey] = false;
    };

    // Pointer left the pedal before release: cancel the timer, no tap.
    const onPedalLeave = (pedalKey) => {
        if (pedalHoldTimers.current[pedalKey]) {
            clearTimeout(pedalHoldTimers.current[pedalKey]);
            pedalHoldTimers.current[pedalKey] = null;
        }
        pedalHoldFired.current[pedalKey] = false;
    };

    // Keyboard access for hold-style pedals (a11y): Space/Enter behave like a
    // physical press down/up; blurring the control cancels without a tap.
    const PEDAL_KEYS = [' ', 'Enter'];
    const onPedalKeyDown = (e, pedalKey) => {
        if (!PEDAL_KEYS.includes(e.key)) return;
        e.preventDefault();
        if (!e.repeat) onPedalDown(pedalKey);
    };
    const onPedalKeyUp = (e, pedalKey) => {
        if (!PEDAL_KEYS.includes(e.key)) return;
        e.preventDefault();
        onPedalUp(pedalKey);
    };

    // Status badge activation (click or keyboard when disconnected).
    const handleBadgeActivate = async () => {
        if (isConnected) return;
        const result = await autoConnect();
        if (!result.success) {
            notify('Connection Failed.\n\nEnsure:\n1. USB cable is active\n2. Device is in MIDI mode (not Transfer mode)', 'error');
        }
    };

    const onEncoderClick = () => {
        if (!isConnected) return;

        const func = currentProfile.encoder.function;
        // Single source of truth for CC numbers: ../constants/functions.js
        // The encoder's "speed" is a CC (speed_cc 18); the footswitch "speed" is a note (17).
        const lookupKey = func === 'speed' ? 'speed_cc' : func;
        const cmd = getMIDICommand(lookupKey);

        // Only CC-type results should be sent from the encoder
        if (cmd && cmd.type === 'cc') {
            const cc = cmd.value;
            // Simulate a "Twist" or "Set to Max" for testing
            // Sending 127 (Max) then 64 (Center) to show movement
            sendControlChange(cc, 127, currentProfile?.midiSettings?.channel || 1);
            setTimeout(() => sendControlChange(cc, 64, currentProfile?.midiSettings?.channel || 1), 200);
        }
    };

    // ...

    const getLEDClass = (status) => {
        switch (status) {
            case 'recording': return 'led-red';
            case 'overdubbing': return 'led-orange';
            case 'playing': return 'led-green';
            case 'muted': return 'led-blue';
            default: return 'led-dim';
        }
    };

    const trackAClass = getLEDClass(deviceState?.trackA?.status || 'stopped');
    const trackBClass = getLEDClass(deviceState?.trackB?.status || 'stopped');

    return (
        <div className="looper-visualization glass">
            <div className="looper-header">
                <h3 className="brand-premium-text">🎸 {deviceType === 'looper-plus' ? 'Ed Sheeran Looper Plus' : 'Ed Sheeran Looper X'}</h3>
                <span
                    className={`status-badge ${isConnected ? 'connected' : 'disconnected'}`}
                    onClick={handleBadgeActivate}
                    onKeyDown={(e) => {
                        if (!isConnected && (e.key === ' ' || e.key === 'Enter')) {
                            e.preventDefault();
                            handleBadgeActivate();
                        }
                    }}
                    role={!isConnected ? 'button' : undefined}
                    tabIndex={!isConnected ? 0 : undefined}
                    aria-label={!isConnected ? 'Not connected. Activate to auto-connect.' : undefined}
                    style={{ cursor: !isConnected ? 'pointer' : 'default' }}
                    title={!isConnected ? "Click to Auto-Connect" : "Connected"}
                >
                    {isConnected ? '✅ Connected' : '⚠️ Not Connected ↻'}
                </span>
            </div>

            {deviceType === 'looper-x' && (
                <div
                    className="looper-x-note"
                    style={{
                        margin: '4px 0 8px',
                        fontSize: '0.8rem',
                        opacity: 0.75,
                        textAlign: 'center'
                    }}
                >
                    Looper X (8 footswitches): full 8-switch support coming soon.
                </div>
            )}



            {/* Hardware Panel */}
            <div className="looper-hardware">

                {/* MAIN ROW: Encoder | LCD | Knobs — one row like the real device */}
                <div className="center-row">

                    {/* 360° nav wheel (left) */}
                    <div className="encoder-container">
                        <div className="encoder-top-labels" aria-hidden="true">
                            <span>• FUNCTION</span>
                            <span>— MENU</span>
                        </div>
                        <div
                            className="encoder-large"
                            onClick={onEncoderClick}
                            onKeyDown={(e) => {
                                if (e.key === ' ' || e.key === 'Enter') {
                                    e.preventDefault();
                                    onEncoderClick();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={`Encoder assigned to ${currentProfile?.encoder?.function || 'Volume'}. Activate to test CC.`}
                            style={{ cursor: 'pointer' }}
                            title={`Encoder: ${currentProfile?.encoder?.function || 'Volume'} (Click to test CC)`}
                        >
                            <div className="encoder-ring"></div>
                            <div className="encoder-notch"></div>
                        </div>
                        <span className="encoder-label">ENCODER</span>
                    </div>


                    {/* LCD */}
                    <div className="lcd-container">
                        <div className="lcd-screen">
                            <div className="lcd-progress" aria-hidden="true"><div /></div>
                            <div className="lcd-track-section">
                                <div className="lcd-track-label">Track A</div>
                                <div className={`led-dot ${trackAClass}`}></div>
                                <div className="lcd-track-status">
                                    {deviceState.trackA.status === 'stopped' ? '' : deviceState.trackA.status.toUpperCase()}
                                </div>
                            </div>
                            <div className="lcd-separator"></div>
                            <div className="lcd-track-section">
                                <div className="lcd-track-label">Track B</div>
                                <div className={`led-dot ${trackBClass}`}></div>
                                <div className="lcd-track-status">
                                    {deviceState.trackB.status === 'stopped' ? '' : deviceState.trackB.status.toUpperCase()}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Knobs + Meters on RIGHT */}
                    <div className="knobs-meters-section">
                        <div className="knob-group">
                            <div className="knob-control disabled">
                                <div className="knob">
                                    <div className="knob-indicator"></div>
                                </div>
                                <span className="knob-label">INST</span>
                            </div>
                            <div className="led-meter-block">
                                <div className="led-meter">
                                    <div className="meter-bar"></div>
                                    <div className="meter-bar peak"></div>
                                    <div className="meter-bar active"></div>
                                    <div className="meter-bar active"></div>
                                </div>
                                <div className="meter-labels" aria-hidden="true">
                                    <span>MAX</span>
                                    <span>-3dB</span>
                                    <span>-6dB</span>
                                    <span>-20dB</span>
                                </div>
                            </div>
                        </div>

                        <div className="knob-group">
                            <div className="knob-control disabled">
                                <div className="knob">
                                    <div className="knob-indicator"></div>
                                </div>
                                <span className="knob-label">MIC</span>
                            </div>
                            <div className="led-meter-block">
                                <div className="led-meter">
                                    <div className="meter-bar"></div>
                                    <div className="meter-bar"></div>
                                    <div className="meter-bar active"></div>
                                    <div className="meter-bar active"></div>
                                </div>
                                <div className="meter-labels" aria-hidden="true">
                                    <span>MAX</span>
                                    <span>-3dB</span>
                                    <span>-6dB</span>
                                    <span>-20dB</span>
                                </div>
                            </div>
                        </div>

                        <div className="knob-group">
                            <div className="knob-control disabled">
                                <div className="knob">
                                    <div className="knob-indicator"></div>
                                </div>
                                <span className="knob-label">MAIN</span>
                            </div>
                            <div className="led-meter-block">
                                <div className="led-meter">
                                    <div className="meter-bar"></div>
                                    <div className="meter-bar active"></div>
                                    <div className="meter-bar active"></div>
                                    <div className="meter-bar active"></div>
                                </div>
                                <div className="meter-labels" aria-hidden="true">
                                    <span>MAX</span>
                                    <span>-3dB</span>
                                    <span>-6dB</span>
                                    <span>-20dB</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MIDDLE: Branding */}
                < div className="branding-row" >
                    <div className="branding">
                        <div className="infinity">∞</div>
                        <div className="brand-name">
                            Sheeran<br />
                            <span className="plus-text">Looper +</span>
                        </div>
                    </div>
                    <div className="hold-hints" aria-hidden="true">
                        <span>— UNDO<br /><i>⁚⁚ TRACK</i></span>
                        <span>— CLEAR<br /><i>⁚⁚ TRACK</i></span>
                    </div>
                </div >

                {/* BOTTOM: Footswitches */}
                < div className="bottom-row" >
                    <div
                        className={`footswitch main-fs ${hoveredControl === 'fs-left' ? 'hovered' : ''}`}
                        onPointerEnter={() => setHoveredControl('fs-left')}
                        onPointerLeave={() => { setHoveredControl(null); onPedalLeave('fs1'); }}
                        onPointerDown={() => onPedalDown('fs1')}
                        onPointerUp={() => onPedalUp('fs1')}
                        onKeyDown={(e) => onPedalKeyDown(e, 'fs1')}
                        onKeyUp={(e) => onPedalKeyUp(e, 'fs1')}
                        onBlur={() => onPedalLeave('fs1')}
                        role="button"
                        tabIndex={0}
                        aria-label={`Footswitch 1: ${currentProfile?.footswitches?.fs1?.function || 'Unknown'}. Hold to trigger hold function.`}
                        title={`FS1: ${currentProfile?.footswitches?.fs1?.function || 'Unknown'}`}
                        style={{ touchAction: 'none' }}
                    >
                        <div className="fs-symbols fs-symbols--left" aria-hidden="true">
                            <span className="fs-icon fs-icon--circle">●</span>
                            <span className="fs-icon fs-icon--plus">+</span>
                            <span className="fs-icon fs-icon--play">▶</span>
                        </div>
                        <div className={`fs-led ${trackAClass}`}></div>
                        <span className="fs-label">REC + PLAY</span>
                    </div>

                    <div
                        className={`footswitch main-fs ${hoveredControl === 'fs-right' ? 'hovered' : ''}`}
                        onPointerEnter={() => setHoveredControl('fs-right')}
                        onPointerLeave={() => { setHoveredControl(null); onPedalLeave('fs2'); }}
                        onPointerDown={() => onPedalDown('fs2')}
                        onPointerUp={() => onPedalUp('fs2')}
                        onKeyDown={(e) => onPedalKeyDown(e, 'fs2')}
                        onKeyUp={(e) => onPedalKeyUp(e, 'fs2')}
                        onBlur={() => onPedalLeave('fs2')}
                        role="button"
                        tabIndex={0}
                        aria-label={`Footswitch 2: ${currentProfile?.footswitches?.fs2?.function || 'Unknown'}. Hold to trigger hold function.`}
                        title={`FS2: ${currentProfile?.footswitches?.fs2?.function || 'Unknown'}`}
                        style={{ touchAction: 'none' }}
                    >
                        <div className="fs-symbols fs-symbols--right" aria-hidden="true">
                            <span className="fs-icon fs-icon--stop">■</span>
                        </div>
                        <div className="fs-led led-dim"></div>
                        <span className="fs-label">STOP</span>
                    </div>
                </div >

                {/* External Pads */}
                < div className="ext-pads" >
                    {currentProfile?.footswitches?.ext1?.enabled && (
                        <div
                            className={`ext-pad ${hoveredControl === 'ext-left' ? 'hovered' : ''}`}
                            onPointerEnter={() => setHoveredControl('ext-left')}
                            onPointerLeave={() => { setHoveredControl(null); onPedalLeave('ext1'); }}
                            onPointerDown={() => onPedalDown('ext1')}
                            onPointerUp={() => onPedalUp('ext1')}
                            onKeyDown={(e) => onPedalKeyDown(e, 'ext1')}
                            onKeyUp={(e) => onPedalKeyUp(e, 'ext1')}
                            onBlur={() => onPedalLeave('ext1')}
                            role="button"
                            tabIndex={0}
                            aria-label={`External footswitch 1: ${currentProfile?.footswitches?.ext1?.function || 'Disabled'}`}
                            title={`EXT1: ${currentProfile?.footswitches?.ext1?.function || 'Disabled'}`}
                            style={{ touchAction: 'none' }}
                        >
                            <span className="ext-label">EXT 1</span>
                        </div>
                    )}
                    {currentProfile?.footswitches?.ext2?.enabled && (
                        <div
                            className={`ext-pad ${hoveredControl === 'ext-right' ? 'hovered' : ''}`}
                            onPointerEnter={() => setHoveredControl('ext-right')}
                            onPointerLeave={() => { setHoveredControl(null); onPedalLeave('ext2'); }}
                            onPointerDown={() => onPedalDown('ext2')}
                            onPointerUp={() => onPedalUp('ext2')}
                            onKeyDown={(e) => onPedalKeyDown(e, 'ext2')}
                            onKeyUp={(e) => onPedalKeyUp(e, 'ext2')}
                            onBlur={() => onPedalLeave('ext2')}
                            role="button"
                            tabIndex={0}
                            aria-label={`External footswitch 2: ${currentProfile?.footswitches?.ext2?.function || 'Disabled'}`}
                            title={`EXT2: ${currentProfile?.footswitches?.ext2?.function || 'Disabled'}`}
                            style={{ touchAction: 'none' }}
                        >
                            <span className="ext-label">EXT 2</span>
                        </div>
                    )}
                </ div >
            </div >

            {/* Program Display */}
            < div className="program-display" >
                <button
                    className="program-nav-btn"
                    onClick={() => {
                        const newProg = Math.max(0, deviceState.currentProgram - 1);
                        // Using sendProgramChange if available in store, otherwise just update state for demo
                        // checking store... useStore has sendProgramChange
                        if (isConnected) useStore.getState().sendProgramChange(newProg, currentProfile?.midiSettings?.channel || 1);
                        else useStore.getState().updateDeviceState({ currentProgram: newProg });
                    }}
                >◀</button>
                <div className="program-info">
                    <span className="program-label">PROGRAM SLOT</span>
                    <span className="program-number">{String(deviceState.currentProgram).padStart(3, '0')}</span>
                </div>
                <button
                    className="program-nav-btn"
                    onClick={() => {
                        const newProg = Math.min(127, deviceState.currentProgram + 1);
                        if (isConnected) useStore.getState().sendProgramChange(newProg, currentProfile?.midiSettings?.channel || 1);
                        else useStore.getState().updateDeviceState({ currentProgram: newProg });
                    }}
                >▶</button>

                {/* Moved Favorites */}
                <div style={{ width: '1px', height: '40px', background: 'rgba(255,255,255,0.1)', margin: '0 5px' }}></div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        className="btn-favorite"
                        onClick={() => {
                            const prog = 100;
                            if (isConnected) useStore.getState().sendProgramChange(prog, currentProfile?.midiSettings?.channel || 1);
                            else useStore.getState().updateDeviceState({ currentProgram: prog });
                        }}
                        title="Load Program Slot 100"
                        style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                    >
                        100
                    </button>
                    <button
                        className="btn-favorite"
                        onClick={() => {
                            const prog = 101;
                            if (isConnected) useStore.getState().sendProgramChange(prog, currentProfile?.midiSettings?.channel || 1);
                            else useStore.getState().updateDeviceState({ currentProgram: prog });
                        }}
                        title="Load Program Slot 101"
                        style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                    >
                        101
                    </button>
                    <button
                        className="btn-favorite"
                        onClick={() => {
                            const prog = 102;
                            if (isConnected) useStore.getState().sendProgramChange(prog, currentProfile?.midiSettings?.channel || 1);
                            else useStore.getState().updateDeviceState({ currentProgram: prog });
                        }}
                        title="Load Program Slot 102"
                        style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                    >
                        102
                    </button>
                </div>
            </div >

            {/* Load / Save Buttons */}
            < div className="program-actions" style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px' }
            }>
                <button
                    className="quick-action-btn btn-program-action"
                    onClick={() => {
                        if (isConnected) {
                            const loadNote = getMIDICommand('load_next_slot').value; // 65 (Load Next Slot, manual 4.5.4)
                            sendNoteOn(loadNote, 127, currentProfile?.midiSettings?.channel || 1);
                            setTimeout(() => sendNoteOff(loadNote, currentProfile?.midiSettings?.channel || 1), 100);
                            // alert(`Loaded Slot ${deviceState.currentProgram}`); // Optional feedback
                        } else {
                            notify("Connect device to Load slot", 'info');
                        }
                    }}
                >
                    LOAD
                </button>
                <button
                    className="quick-action-btn btn-program-action"
                    style={{ width: '100px', cursor: 'not-allowed', opacity: 0.3, background: 'rgba(255,255,255,0.05)', color: '#aaa', border: '1px solid #444' }}
                    title="Save not supported via MIDI"
                >
                    SAVE
                </button>
            </div >




            {/* Quick Actions Toolbar */}
            {/* Desktop Controls Interface */}
            <div className="desktop-controls-container">
                <div className="main-control-area">
                    {/* LEFT: TRACK A (Green) */}
                    <div className="control-panel panel-a">
                        <div className="panel-label">TRACK A</div>
                        <QuickActionButton
                            note={getMIDICommand('rec_play').value} // 11
                            label="REC / PLAY A"
                            desc="Record > Play > Overdub (Cycle)"
                            className={`btn-main btn-green ${['playing', 'recording', 'overdubbing'].includes(deviceState.trackA.status) ? 'active-glow' : ''}`}
                            icon={<div className="rec-play-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}><FaCircle size={80} /><FaPlay size={100} /></div>}
                            sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                        />
                        <div className="sub-controls">
                            <QuickActionButton
                                note={getMIDICommand('select_track_a').value} // 78 (Select Track A, manual 4.5.4)
                                label="SELECT A"
                                desc="Track A Select"
                                className={`btn-sub btn-green ${deviceState.trackA.select ? 'active-glow' : ''}`}
                                icon={<FaHandPointer size={72} />}
                                sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                            />
                            <QuickActionButton
                                note={getMIDICommand('mute_track_a').value} // 70
                                label="MUTE A"
                                desc="Track A Mute"
                                className={`btn-sub btn-green ${deviceState.trackA.status === 'muted' ? 'state-muted' : ''}`}
                                icon={<FaMicrophoneSlash size={72} />}
                                sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                            />
                        </div>
                        <HoldActionButton
                            note={getMIDICommand('clear').value} // 49 (Clear, manual 4.5.4)
                            label="CLEAR TRACK"
                            className="btn-clear-track"
                            icon={<FaTrash size={24} />}
                            sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                        />
                    </div>

                    {/* CENTER: MODIFIERS */}
                    <div className="control-panel center-column">
                        <div className="performance-section">
                        <QuickActionButton
                            note="stop_all"
                            label="STOP ALL"
                            className="btn-main-stop btn-red"
                            icon={<FaStop size={80} />}
                            sendNoteOn={sendNoteOn}
                            sendNoteOff={sendNoteOff}
                            channel={currentProfile?.midiSettings?.channel}
                        />
                            <div className="toggles-row">
                                <QuickActionButton
                                    note={getMIDICommand('reverse').value} // 29
                                    label="REVERSE"
                                    className="btn-rect btn-purple"
                                    isActive={reverseActive}
                                    onToggle={() => setReverseActive(!reverseActive)}
                                    icon={<FaBackward size={64} />}
                                    sendNoteOn={sendNoteOn}
                                    sendNoteOff={sendNoteOff}
                                    channel={currentProfile?.midiSettings?.channel}
                                />
                                <QuickActionButton
                                    note={getMIDICommand('speed').value} // 17
                                    label="SPEED"
                                    className="btn-rect btn-orange"
                                    isActive={speedActive}
                                    onToggle={() => setSpeedActive(!speedActive)}
                                    icon={<FaForward size={64} />}
                                    sendNoteOn={sendNoteOn}
                                    sendNoteOff={sendNoteOff}
                                    channel={currentProfile?.midiSettings?.channel}
                                />
                            </div>


                        </div>
                    </div>

                    {/* RIGHT: TRACK B (Blue) */}
                    <div className="control-panel panel-b">
                        <div className="panel-label">TRACK B</div>
                        <QuickActionButton
                            note={getMIDICommand('rec_play_b').value} // 12
                            label="REC / PLAY B"
                            desc="Record > Play > Overdub (Cycle)"
                            className={`btn-main btn-blue ${['playing', 'recording', 'overdubbing'].includes(deviceState.trackB.status) ? 'active-glow' : ''}`}
                            icon={<div className="rec-play-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}><FaCircle size={80} /><FaPlay size={100} /></div>}
                            sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                        />
                        <div className="sub-controls">
                            <QuickActionButton
                                note={getMIDICommand('select_track_b').value} // 79 (Select Track B, manual 4.5.4)
                                label="SELECT B"
                                desc="Track B Select"
                                className="btn-sub btn-blue"
                                icon={<FaHandPointer size={72} />}
                                sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                            />
                            <QuickActionButton
                                note={getMIDICommand('mute_track_b').value} // 71
                                label="MUTE B"
                                desc="Track B Mute"
                                className={`btn-sub btn-blue ${deviceState.trackB.status === 'muted' ? 'state-muted' : ''}`}
                                icon={<FaMicrophoneSlash size={72} />}
                                sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                            />
                        </div>
                        <HoldActionButton
                            note={getMIDICommand('clear').value} // 49 (Clear, manual 4.5.4)
                            label="CLEAR TRACK"
                            className="btn-clear-track"
                            icon={<FaTrash size={24} />}
                            sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                        />
                    </div>
                </div>

                {/* BOTTOM: SAFETY */}
                <div className="bottom-safety-bar">
                    <div className="safety-left">
                        {/* The manual defines ONE Undo/Redo note (45) — each press
                            toggles undo/redo on the hardware, so there is a single
                            button, not separate UNDO and REDO. */}
                        <QuickActionButton
                            note={getMIDICommand('undo_redo').value} // undo_redo = 45 (manual 4.5.4)
                            label="UNDO / REDO"
                            className="btn-square btn-gray safety-left"
                            icon={<FaRotateLeft size={56} />}
                            sendNoteOn={sendNoteOn} sendNoteOff={sendNoteOff} channel={currentProfile?.midiSettings?.channel}
                        />
                    </div>
                    {/* Mode Button Moved Here */}
                    <div className="safety-center" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <button
                            className="quick-action-btn btn-rect btn-blue btn-mode-large active-glow"
                            title={`Current Mode: ${currentMode.name}. Click to switch to ${nextMode.name}`}
                            onClick={() => {
                                const ch = currentProfile?.midiSettings?.channel || 1;

                                // Switch mode ONLY. Never send Clear Session (note 49) here —
                                // it wipes all recorded loops on hardware; Clear is reserved
                                // for the deliberate hold-to-confirm button.
                                sendNoteOn(nextMode.note, 127, ch);
                                setTimeout(() => sendNoteOff(nextMode.note, ch), 100);
                                setCurrentModeName(nextMode.name);
                            }}
                        >
                            {currentMode.name.toUpperCase()}
                        </button>
                        <span style={{ marginTop: '2px', fontSize: '10px', color: '#888', letterSpacing: '1px' }}>MODE</span>
                    </div>
                    <div className="safety-right">
                        <HoldActionButton
                            note={getMIDICommand('clear').value} // Clear Session (49)
                            label="CLEAR SESSION"
                            className="btn-rect btn-red btn-clear-large safety-right"
                            icon={<FaTrash size={60} />}
                            sendNoteOn={sendNoteOn}
                            sendNoteOff={sendNoteOff}
                            channel={currentProfile?.midiSettings?.channel}
                        />
                    </div>
                </div>
            </div>
        </div >
    );
}

function HoldActionButton({ note, label, className, sendNoteOn, sendNoteOff, channel, icon = null }) {
    const [progress, setProgress] = useState(0);
    // Live rAF id + cancel flag in refs (NOT state): a render-closure id lags the
    // running animation by at least one frame, which let quick taps slip through
    // and fire the (destructive) hold action anyway.
    const rafIdRef = useRef(null);
    const cancelledRef = useRef(false);
    // The post-success visual reset must be cancellable: if it fired during a
    // fast re-press it would null the live rAF id mid-animation.
    const resetTimeoutRef = useRef(null);

    const cancelHold = () => {
        cancelledRef.current = true;
        setProgress(0);
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    };

    const triggerAction = () => {
        const ch = channel || 1;
        sendNoteOn(note, 127, ch);
        setTimeout(() => sendNoteOff(note, ch), 100); // Quick tap

        // Reset after success. Small delay to show the full bar. Skips itself
        // if a new hold has already started within the window.
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => {
            resetTimeoutRef.current = null;
            if (rafIdRef.current !== null) return; // a new hold is running
            setProgress(0);
            cancelledRef.current = false;
        }, 200);
    };

    // Cancel any in-flight hold if the button unmounts mid-press.
    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
            clearTimeout(resetTimeoutRef.current);
        };
    }, []);

    const handleMouseDown = () => {
        // Reset state for a fresh press.
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
        cancelledRef.current = false;
        if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);

        const startTime = Date.now();
        const duration = 1000;

        // Animation loop
        const animate = () => {
            if (cancelledRef.current) return;

            const elapsed = Date.now() - startTime;
            const pct = Math.min((elapsed / duration) * 100, 100);
            setProgress(pct);

            if (elapsed < duration) {
                rafIdRef.current = requestAnimationFrame(animate);
            } else {
                rafIdRef.current = null;
                triggerAction();
            }
        };

        rafIdRef.current = requestAnimationFrame(animate);
    };

    const handleMouseUp = () => {
        cancelHold();
    };

    const handleMouseLeave = () => {
        cancelHold();
    };

    // Keyboard hold (a11y): holding Space/Enter runs the same fill; releasing
    // early cancels exactly like mouse-up. The component sets no onClick, so
    // the native keyup-click cannot double-fire the action.
    const handleKeyDown = (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            handleMouseDown();
        }
    };
    const handleKeyUp = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            cancelHold();
        }
    };

    return (
        <button
            className={`quick-action-btn ${className} hold-btn`}
            onPointerDown={handleMouseDown}
            onPointerUp={handleMouseUp}
            onPointerLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            aria-label={`Hold for 1 second to ${label}`}
            style={{ position: 'relative', overflow: 'hidden', touchAction: 'none' }}
            title="Hold for 1 second to Clear"
        >
            {icon ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', zIndex: 2, position: 'relative', pointerEvents: 'none' }}>
                    <span className="btn-icon">{icon}</span>
                    <span className="btn-label-small" style={{ fontSize: '0.6em', opacity: 0.8, textTransform: 'uppercase' }}>{label}</span>
                </div>
            ) : (
                <span style={{ position: 'relative', zIndex: 2 }}>{label}</span>
            )}
            <div
                className="progress-overlay"
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '100%',
                    width: `${progress}%`,
                    background: 'rgba(255, 255, 255, 0.3)',
                    transition: 'width 0.05s linear',
                    zIndex: 1
                }}
            ></div>
        </button>
    );
}

export default LooperVisualization;
